import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import {
  Home,
  Users,
  FileText,
  Activity,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  Building,
  DollarSign,
  Phone,
  Check,
  X,
  ArrowRightLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_deal-dashboard-16/artifacts/mtal5hnl_0e8de284-294a-4a25-8f18-7d7cab9aad20.png";

const AdminDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("properties");
  const [stats, setStats] = useState({});
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);

  const [editingProperty, setEditingProperty] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [propertyForm, setPropertyForm] = useState({
    address: "",
    city: "",
    state: "MI",
    zip_code: "",
    price: "",
    description: "",
    bedrooms: 0,
    bathrooms: 0,
    sqft: 0,
  });

  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "client",
  });

  const [assignForm, setAssignForm] = useState({
    client_ids: [],
    phone_visible: false,
    title_work_visible: false,
  });

  const [offerResponse, setOfferResponse] = useState({
    action: "",
    counter_amount: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, propertiesRes, usersRes, logsRes, offersRes] =
        await Promise.all([
          axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/properties`, { headers: getAuthHeaders() }),
          axios.get(`${API}/users`, { headers: getAuthHeaders() }),
          axios.get(`${API}/activity-logs?limit=50`, { headers: getAuthHeaders() }),
          axios.get(`${API}/offers`, { headers: getAuthHeaders() }),
        ]);

      setStats(statsRes.data);
      setProperties(propertiesRes.data);
      setUsers(usersRes.data);
      setActivityLogs(logsRes.data);
      setOffers(offersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCreateProperty = async () => {
    try {
      await axios.post(
        `${API}/properties`,
        {
          ...propertyForm,
          price: parseFloat(propertyForm.price) || 0,
          bedrooms: parseInt(propertyForm.bedrooms) || 0,
          bathrooms: parseFloat(propertyForm.bathrooms) || 0,
          sqft: parseInt(propertyForm.sqft) || 0,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Property created!");
      setShowPropertyModal(false);
      resetPropertyForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create property");
    }
  };

  const handleUpdateProperty = async () => {
    try {
      await axios.put(
        `${API}/properties/${editingProperty.id}`,
        {
          ...propertyForm,
          price: parseFloat(propertyForm.price) || 0,
          bedrooms: parseInt(propertyForm.bedrooms) || 0,
          bathrooms: parseFloat(propertyForm.bathrooms) || 0,
          sqft: parseInt(propertyForm.sqft) || 0,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Property updated!");
      setShowPropertyModal(false);
      setEditingProperty(null);
      resetPropertyForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to update property");
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Property deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const deletePropertyImage = async (propertyId, imageId) => {
    if (!window.confirm("Delete image?")) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}/images/${imageId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Image deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const resetPropertyForm = () => {
    setPropertyForm({
      address: "",
      city: "",
      state: "MI",
      zip_code: "",
      price: "",
      description: "",
      bedrooms: 0,
      bathrooms: 0,
      sqft: 0,
    });
  };

  const openEditProperty = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      price: property.price,
      description: property.description || "",
      bedrooms: property.bedrooms || 0,
      bathrooms: property.bathrooms || 0,
      sqft: property.sqft || 0,
    });
    setShowPropertyModal(true);
  };

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/users`, userForm, { headers: getAuthHeaders() });
      toast.success("User created!");
      setShowUserModal(false);
      setUserForm({ name: "", email: "", password: "", role: "client" });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success("User deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const openAssignModal = (property) => {
    setSelectedProperty(property);
    setAssignForm({
      client_ids: property.assigned_clients || [],
      phone_visible: false,
      title_work_visible: false,
    });
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    try {
      await axios.post(
        `${API}/properties/${selectedProperty.id}/assign`,
        assignForm,
        { headers: getAuthHeaders() }
      );
      toast.success("Clients assigned!");
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign clients");
    }
  };

  const handleVisibilityUpdate = async (
    propertyId,
    clientId,
    phoneVisible,
    titleVisible,
    propertyVisible
  ) => {
    try {
      await axios.post(
        `${API}/properties/${propertyId}/visibility/${clientId}`,
        {
          phone_visible: phoneVisible,
          title_work_visible: titleVisible,
          property_visible: propertyVisible,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Visibility updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleOfferResponse = async () => {
    if (!selectedOffer || !offerResponse.action) return;
    try {
      await axios.post(
        `${API}/offers/${selectedOffer.id}/respond`,
        {
          offer_id: selectedOffer.id,
          action: offerResponse.action,
          counter_amount:
            offerResponse.action === "counter"
              ? parseFloat(offerResponse.counter_amount)
              : null,
          notes: offerResponse.notes,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Offer ${offerResponse.action}!`);
      setShowOfferModal(false);
      setSelectedOffer(null);
      setOfferResponse({ action: "", counter_amount: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to respond to offer");
    }
  };

  const clients = users.filter((u) => u.role === "client");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-brand-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Mitten Deals" className="h-10 w-auto" />
              <span className="hidden sm:block text-sm font-medium text-slate-300">
                Admin Dashboard
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-300">Welcome, {user?.name}</span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card-surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-orange/10 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-brand-orange" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-primary">
                  {stats.total_properties || 0}
                </p>
                <p className="text-xs text-slate-500">Total Properties</p>
              </div>
            </div>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-primary">
                  {stats.available_properties || 0}
                </p>
                <p className="text-xs text-slate-500">Available</p>
              </div>
            </div>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-primary">
                  {stats.under_contract || 0}
                </p>
                <p className="text-xs text-slate-500">Under Contract</p>
              </div>
            </div>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-primary">
                  {stats.total_clients || 0}
                </p>
                <p className="text-xs text-slate-500">Clients</p>
              </div>
            </div>
          </div>

          <div className="card-surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-primary">
                  {stats.recent_activity || 0}
                </p>
                <p className="text-xs text-slate-500">Weekly Activity</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="properties" data-testid="properties-tab">
              <Building className="w-4 h-4 mr-2" /> Properties
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab">
              <DollarSign className="w-4 h-4 mr-2" /> Offers
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab">
              <Activity className="w-4 h-4 mr-2" /> Activity Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="properties">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-brand-primary">Properties</h2>
              <Button
                onClick={() => {
                  resetPropertyForm();
                  setEditingProperty(null);
                  setShowPropertyModal(true);
                }}
                className="btn-primary"
                data-testid="add-property-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Property
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <div
                  key={property.id}
                  className="card-surface overflow-hidden"
                  data-testid={`property-card-${property.id}`}
                >
                  <div className="h-40 bg-slate-200 relative">
                    {property.images?.length > 0 ? (
                      <>
                        <img
                          src={`${API}/files/${property.images[0].storage_path}?auth=${localStorage.getItem("token")}`}
                          alt={property.address}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePropertyImage(property.id, property.images[0].id);
                          }}
                          className="absolute top-2 left-2 z-10 bg-red-600 text-white px-2 py-1 text-xs rounded"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Home className="w-12 h-12" />
                      </div>
                    )}

                    <div
                      className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold ${
                        property.under_contract
                          ? "bg-yellow-500 text-white"
                          : "bg-green-500 text-white"
                      }`}
                    >
                      {property.under_contract ? "Under Contract" : "Available"}
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-brand-primary truncate">
                      {property.address}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {property.city}, {property.state} {property.zip_code}
                    </p>
                    <p className="text-lg font-bold text-brand-orange mt-2">
                      ${property.price?.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                      <span>{property.bedrooms} bed</span>
                      <span>{property.bathrooms} bath</span>
                      <span>{property.sqft?.toLocaleString()} sqft</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                      <Users className="w-3 h-3" />
                      <span>{property.assigned_clients?.length || 0} assigned</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/property/${property.id}`)}
                        data-testid={`view-property-${property.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditProperty(property)}
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAssignModal(property)}
                      >
                        <Users className="w-3 h-3 mr-1" /> Assign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedProperty(property);
                          setShowVisibilityModal(true);
                        }}
                        data-testid={`visibility-${property.id}`}
                      >
                        <EyeOff className="w-3 h-3 mr-1" /> Visibility
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteProperty(property.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {properties.length === 0 && (
              <div className="card-surface p-12 text-center">
                <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No properties yet. Add your first property!</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="users">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-brand-primary">Users</h2>
              <Button
                onClick={() => {
                  setUserForm({ name: "", email: "", password: "", role: "client" });
                  setShowUserModal(true);
                }}
                className="btn-primary"
                data-testid="add-user-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </div>

            <div className="card-surface overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Name</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Email</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Role</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-brand-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b hover:bg-slate-50"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-4 py-3">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{u.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            u.role === "admin"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {u.role !== "admin" && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteUser(u.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <h2 className="text-xl font-bold text-brand-primary mb-4">Activity Logs</h2>
            <div className="card-surface">
              <ScrollArea className="h-[500px]">
                <div className="divide-y">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="px-4 py-3 flex items-start gap-4">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Activity className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-primary">{log.action}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {JSON.stringify(log.details)}
                        </p>
                      </div>
                      <div className="text-xs text-slate-400 flex-shrink-0">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="offers">
            <h2 className="text-xl font-bold text-brand-primary mb-4">Offers</h2>
            {offers.length === 0 ? (
              <div className="card-surface p-12 text-center">
                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No offers received yet.</p>
              </div>
            ) : (
              <div className="card-surface overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Property</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Client</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Offer</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-brand-primary">Date</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-brand-primary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map((offer) => {
                      const prop = properties.find((p) => p.id === offer.property_id);
                      return (
                        <tr key={offer.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{prop?.address || "Unknown"}</p>
                            <p className="text-xs text-slate-500">{prop?.city}, {prop?.state}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm">{offer.client_name}</p>
                            <p className="text-xs text-slate-500">{offer.client_email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-bold text-brand-orange">${offer.amount?.toLocaleString()}</p>
                            {offer.counter_amount && (
                              <p className="text-xs text-slate-500">
                                Counter: ${offer.counter_amount?.toLocaleString()}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                offer.status === "pending"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : offer.status === "accepted"
                                    ? "bg-green-100 text-green-700"
                                    : offer.status === "counter"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-red-100 text-red-700"
                              }`}
                            >
                              {offer.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {new Date(offer.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {offer.status === "pending" && (
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    setSelectedOffer(offer);
                                    setOfferResponse({
                                      action: "accepted",
                                      counter_amount: "",
                                      notes: "",
                                    });
                                    setShowOfferModal(true);
                                  }}
                                  data-testid={`accept-offer-${offer.id}`}
                                >
                                  <Check className="w-3 h-3" />
                                </Button>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedOffer(offer);
                                    setOfferResponse({
                                      action: "counter",
                                      counter_amount: "",
                                      notes: "",
                                    });
                                    setShowOfferModal(true);
                                  }}
                                  data-testid={`counter-offer-${offer.id}`}
                                >
                                  <ArrowRightLeft className="w-3 h-3" />
                                </Button>

                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedOffer(offer);
                                    setOfferResponse({
                                      action: "rejected",
                                      counter_amount: "",
                                      notes: "",
                                    });
                                    setShowOfferModal(true);
                                  }}
                                  data-testid={`reject-offer-${offer.id}`}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProperty ? "Edit Property" : "Add Property"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Address</Label>
              <Input
                value={propertyForm.address}
                onChange={(e) =>
                  setPropertyForm({ ...propertyForm, address: e.target.value })
                }
                className="input-field"
                data-testid="property-address-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={propertyForm.city}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, city: e.target.value })
                  }
                  className="input-field"
                  data-testid="property-city-input"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={propertyForm.state}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, state: e.target.value })
                  }
                  className="input-field"
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={propertyForm.zip_code}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, zip_code: e.target.value })
                  }
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <Label>Price ($)</Label>
              <Input
                type="number"
                value={propertyForm.price}
                onChange={(e) =>
                  setPropertyForm({ ...propertyForm, price: e.target.value })
                }
                className="input-field"
                data-testid="property-price-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Bedrooms</Label>
                <Input
                  type="number"
                  value={propertyForm.bedrooms}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, bedrooms: e.target.value })
                  }
                  className="input-field"
                />
              </div>
              <div>
                <Label>Bathrooms</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={propertyForm.bathrooms}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, bathrooms: e.target.value })
                  }
                  className="input-field"
                />
              </div>
              <div>
                <Label>Sqft</Label>
                <Input
                  type="number"
                  value={propertyForm.sqft}
                  onChange={(e) =>
                    setPropertyForm({ ...propertyForm, sqft: e.target.value })
                  }
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={propertyForm.description}
                onChange={(e) =>
                  setPropertyForm({
                    ...propertyForm,
                    description: e.target.value,
                  })
                }
                className="input-field min-h-[80px]"
              />
            </div>

            <Button
              onClick={editingProperty ? handleUpdateProperty : handleCreateProperty}
              className="btn-primary w-full"
              data-testid="save-property-button"
            >
              {editingProperty ? "Update Property" : "Create Property"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="input-field"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="input-field"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) =>
                  setUserForm({ ...userForm, password: e.target.value })
                }
                className="input-field"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger className="input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCreateUser}
              className="btn-primary w-full"
              data-testid="save-user-button"
            >
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Select clients who can view this property:
            </p>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center gap-3 p-2 rounded hover:bg-slate-50"
                >
                  <Checkbox
                    checked={assignForm.client_ids.includes(client.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setAssignForm({
                          ...assignForm,
                          client_ids: [...assignForm.client_ids, client.id],
                        });
                      } else {
                        setAssignForm({
                          ...assignForm,
                          client_ids: assignForm.client_ids.filter(
                            (id) => id !== client.id
                          ),
                        });
                      }
                    }}
                  />
                  <div>
                    <p className="text-sm font-medium">{client.name}</p>
                    <p className="text-xs text-slate-500">{client.email}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={assignForm.phone_visible}
                  onCheckedChange={(checked) =>
                    setAssignForm({ ...assignForm, phone_visible: checked })
                  }
                />
                <Label>Show phone number to assigned clients</Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={assignForm.title_work_visible}
                  onCheckedChange={(checked) =>
                    setAssignForm({ ...assignForm, title_work_visible: checked })
                  }
                />
                <Label>Show title work to assigned clients</Label>
              </div>
            </div>

            <Button
              onClick={handleAssign}
              className="btn-primary w-full"
              data-testid="save-assign-button"
            >
              Save Assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVisibilityModal} onOpenChange={setShowVisibilityModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Manage Client Visibility - {selectedProperty?.address}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Toggle visibility settings for each assigned client:
            </p>

            {selectedProperty &&
              clients
                .filter((c) => selectedProperty.assigned_clients?.includes(c.id))
                .map((client) => (
                  <div key={client.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <p className="text-xs text-slate-500">{client.email}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-slate-500" />
                          <span className="text-xs">Property</span>
                        </div>
                        <Switch
                          checked={selectedProperty.visible_to?.includes(client.id)}
                          onCheckedChange={(checked) =>
                            handleVisibilityUpdate(
                              selectedProperty.id,
                              client.id,
                              selectedProperty.phone_visible_to?.includes(client.id),
                              selectedProperty.title_work_visible_to?.includes(client.id),
                              checked
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-500" />
                          <span className="text-xs">Phone</span>
                        </div>
                        <Switch
                          checked={selectedProperty.phone_visible_to?.includes(client.id)}
                          onCheckedChange={(checked) =>
                            handleVisibilityUpdate(
                              selectedProperty.id,
                              client.id,
                              checked,
                              selectedProperty.title_work_visible_to?.includes(client.id),
                              selectedProperty.visible_to?.includes(client.id)
                            )
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-xs">Title Work</span>
                        </div>
                        <Switch
                          checked={selectedProperty.title_work_visible_to?.includes(client.id)}
                          onCheckedChange={(checked) =>
                            handleVisibilityUpdate(
                              selectedProperty.id,
                              client.id,
                              selectedProperty.phone_visible_to?.includes(client.id),
                              checked,
                              selectedProperty.visible_to?.includes(client.id)
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}

            {selectedProperty &&
              (!selectedProperty.assigned_clients ||
                selectedProperty.assigned_clients.length === 0) && (
                <p className="text-center text-slate-500 py-4">
                  No clients assigned to this property.
                </p>
              )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {offerResponse.action === "accepted" && "Accept Offer"}
              {offerResponse.action === "counter" && "Counter Offer"}
              {offerResponse.action === "rejected" && "Reject Offer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedOffer && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Original Offer:</p>
                <p className="text-2xl font-bold text-brand-orange">
                  ${selectedOffer.amount?.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  From: {selectedOffer.client_name}
                </p>
              </div>
            )}

            {offerResponse.action === "counter" && (
              <div>
                <Label>Counter Amount ($)</Label>
                <Input
                  type="number"
                  value={offerResponse.counter_amount}
                  onChange={(e) =>
                    setOfferResponse({
                      ...offerResponse,
                      counter_amount: e.target.value,
                    })
                  }
                  className="input-field"
                  placeholder="Enter counter offer amount"
                  data-testid="counter-amount-input"
                />
              </div>
            )}

            <div>
              <Label>Notes (optional)</Label>
              <textarea
                value={offerResponse.notes}
                onChange={(e) =>
                  setOfferResponse({ ...offerResponse, notes: e.target.value })
                }
                className="input-field min-h-[80px]"
                placeholder="Add any notes for the client..."
              />
            </div>

            <Button
              onClick={handleOfferResponse}
              className={`w-full ${
                offerResponse.action === "accepted"
                  ? "bg-green-600 hover:bg-green-700"
                  : offerResponse.action === "rejected"
                    ? "bg-red-600 hover:bg-red-700"
                    : "btn-primary"
              }`}
              data-testid="confirm-offer-response"
            >
              {offerResponse.action === "accepted" && "Accept Offer"}
              {offerResponse.action === "counter" && "Send Counter Offer"}
              {offerResponse.action === "rejected" && "Reject Offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;